
import { ChatRequest } from "./types";
import { getAuthConfig } from "@/pages/taskpane/features/auth/config/authConfig";
// Logger utility - safe check for development mode
const isDevelopment = typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development';

const logger = {
    debug: (...args: any[]) => {
        if (isDevelopment) {
            console.debug(...args);
        }
    },
    warn: (...args: any[]) => {
        if (isDevelopment) {
            console.warn(...args);
        }
    },
    error: (...args: any[]) => {
        console.error(...args);
    }
};

// Use environment variable or fallback to baseURL
const getApiUrl = () => {
    const authConfig = getAuthConfig();
    // Check for environment variable first (safe check)
    if (authConfig.endpointUrl) {
        return `${authConfig.endpointUrl}/RFMultiAgents/chat/stream`;
    } else {
        return 'http://localhost:8000/chat/stream';
    }
};

const API_URL = getApiUrl();


export type StreamUpdate =
    | { type: 'append'; content: string }
    | { type: 'replace'; content: string }
    | { type: 'thinking'; content: string };

export const streamChat = async (
    payload: ChatRequest,
    onUpdate: (update: StreamUpdate) => void,
    onError: (error: any) => void,
    onComplete: () => void,
    token?: string
) => {
    try {
        // Build headers object
        const headers: HeadersInit = {
            "accept": "text/event-stream",
            "accept-language": "en-US,en;q=0.9",
            "cache-control": "no-cache",
            "content-type": "application/json",
            "pragma": "no-cache",
            "priority": "u=1, i",
        };

        // Add Authorization header if token is provided (highest priority)
        if (token) {
            headers["Authorization"] = `Bearer ${token}`;
        } else {
            logger.warn('[Token Flow] FE: No token provided for Authorization header');
        }

        const response = await fetch(API_URL, {
            method: "POST",
            headers,
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            throw new Error(`API Error: ${response.status} ${response.statusText}`);
        }

        if (!response.body) {
            throw new Error("Response body is empty");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";

        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                break;
            }
            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;

            const lines = buffer.split("\n");
            // Keep the last line in the buffer if it's incomplete
            buffer = lines.pop() || "";

            for (const line of lines) {
                const trimmedLine = line.trim();
                if (!trimmedLine.startsWith("data: ")) {
                    continue;
                }

                try {
                    const jsonStr = trimmedLine.substring(6);
                    const data = JSON.parse(jsonStr);

                    if (data.type === "token" && data.content) {
                        onUpdate({ type: 'append', content: data.content });
                    } else if (data.type === "final" && data.response) {
                        onUpdate({ type: 'replace', content: data.response });
                    } else if (data.type === "agent_start" || data.type === "tool_start" || data.type === "llm_thinking") {
                        if (data.thought) {
                            onUpdate({ type: 'thinking', content: data.thought });
                        }
                    }
                } catch (e) {
                    logger.error("Error parsing JSON from stream:", e);
                }
            }
        }

        onComplete();

    } catch (error) {
        logger.error("Stream chat error:", error);
        onError(error);
    }
};
